import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Avatar } from "@/components/Avatar";
import { Icon } from "@/components/Icon";
import { MetricCard } from "@/components/MetricCard";
import { Pill } from "@/components/Pill";
import { PriorityChip } from "@/components/PriorityChip";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

/**
 * Person details — placeholder page rendered from existing /api/team data.
 *
 * NOTE: there is no per-user endpoint today, so this page filters the team
 * list + global tasks list to derive a person view. When a real
 * /api/team/:id endpoint lands, swap `api.teamMember` to call it directly.
 */
function PersonDetailsPage() {
  const { userId } = Route.useParams();

  const memberQ = useQuery({
    queryKey: queryKeys.teamMember(userId),
    queryFn: () => api.teamMember(userId),
  });
  const workloadQ = useQuery({
    queryKey: queryKeys.teamWorkload(),
    queryFn: api.teamWorkload,
  });
  const tasksQ = useQuery({
    queryKey: queryKeys.tasksAll(),
    queryFn: api.tasksAll,
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });

  const projectsMap = useMemo(() => {
    const map = new Map<string, { name: string; color: string | null }>();
    for (const p of projectsQ.data?.projects ?? []) {
      map.set(p.id, { name: p.name, color: p.color ?? null });
    }
    return map;
  }, [projectsQ.data]);

  const workload =
    workloadQ.data?.members.find((m) => m.userId === userId) ?? null;

  const personTasks = (tasksQ.data?.items ?? []).filter((t) =>
    t.assigneeIds?.includes(userId),
  );

  const recentTasks = useMemo(() => {
    const sorted = [...personTasks].sort((a, b) => {
      const aT = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bT = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bT - aT;
    });
    return sorted.slice(0, 10);
  }, [personTasks]);

  if (memberQ.isLoading) {
    return (
      <div className="pg-wrap">
        <Skeleton count={4} height={48} />
      </div>
    );
  }

  if (memberQ.isError || !memberQ.data) {
    return (
      <div className="pg-wrap">
        <EmptyState
          icon="close"
          title="Couldn't load this person."
          caption="Refresh the page or try again in a moment."
          actionLabel="Back to team"
          onAction={() => {
            window.location.href = "/team";
          }}
        />
      </div>
    );
  }

  const member = memberQ.data;
  const displayName = member.name ?? member.email;

  const role = member.role === "admin" ? "Admin" : "Member";

  // Stats: prefer workload (server-computed) but fall back to filtering
  // tasksAll for the assignee. completedCount filter relies on status.
  const assignedCount =
    workload?.openCount !== undefined
      ? workload.openCount + workload.completedCount
      : personTasks.length;
  const completedCount =
    workload?.completedCount ??
    personTasks.filter((t) => t.status === "done").length;

  return (
    <div className="pg-wrap">
      <header className="pg-head">
        <Link
          to="/team"
          className="ph-link"
          style={{
            marginRight: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <Icon name="arrow" size={14} />
          Team
        </Link>
        <h1>{displayName}</h1>
      </header>

      <div
        className="panel"
        style={{ display: "flex", alignItems: "center", gap: 16, padding: 20 }}
      >
        <Avatar id={member.id} name={displayName} size="lg" />
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 20, fontWeight: 600 }}>{displayName}</div>
          <div className="t-sm t-muted">{member.email}</div>
          <div className="t-xs t-muted">{role}</div>
        </div>
      </div>

      <div
        className="metric-row"
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 16,
        }}
      >
        <MetricCard label="Assigned tasks" value={assignedCount} />
        <MetricCard label="Completed" value={completedCount} />
        <MetricCard
          label="Projects"
          value={workload?.projectCount ?? member.projects?.length ?? "—"}
        />
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-head">
          <h3>Recent tasks</h3>
        </div>
        {tasksQ.isLoading ? (
          <div style={{ padding: "16px 20px" }}>
            <Skeleton count={4} height={42} />
          </div>
        ) : recentTasks.length === 0 ? (
          <p
            className="t-sm"
            style={{ padding: "16px 20px", color: "var(--muted)" }}
          >
            No tasks assigned.
          </p>
        ) : (
          <div>
            {recentTasks.map((t) => {
              const proj = projectsMap.get(t.projectId);
              return (
                <Link
                  key={t.id}
                  to="/tasks/$taskId"
                  params={{ taskId: t.id }}
                  className="task-row"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 120px 110px 168px",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 20px",
                    borderTop: "1px solid var(--border)",
                  }}
                >
                  <span
                    className="tr-title"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.title}
                  </span>
                  <Pill
                    variant={
                      t.status === "backlog" ||
                      t.status === "todo" ||
                      t.status === "doing" ||
                      t.status === "done"
                        ? t.status
                        : "backlog"
                    }
                    dot
                  >
                    {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                  </Pill>
                  <PriorityChip level={t.priority} />
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      color: "var(--muted)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      className="proj-dot"
                      style={{
                        width: 8,
                        height: 8,
                        background: proj?.color ?? "var(--muted)",
                      }}
                    />
                    {proj?.name ?? t.projectId.slice(0, 8)}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/team/$userId")({
  component: PersonDetailsPage,
});
